import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../../api/apiFactory";
import { showNotification } from "../notificationSlice";
const itemApi = factory.get("item");
const itemPriceApi = factory.get("itemPrice");

const initialState = {
  items: {
    data: [],
    loading: false,
    total: 0,
    filters: {
      skip: 0,
      page: 0,
      take: 25,
      bar_code: "",
    },
  },
  item: {
    dialog: false,
    loading: false,
    form: {
      id: "",
      name: "",
      unite: "",
      price: 0,
      description: "",
      bar_code: "",
      required_serial: false,
    },
    errors: {},
  },
  priceDialog: {
    open: false,
    item_id: "",
    data: [],
    loading: false,
    form: {
      id: "",
      name: "",
      min: "",
      max: "",
      price: "",
    },
  },
};

const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    setItems: (state, { payload }) => {
      state.items.data = payload.data;
      state.items.total = payload.total;
    },
    setLoading: (state, { payload }) => {
      state[payload].loading = !state[payload].loading;
    },
    setFilters: (state, { payload }) => {
      state.items.filters = {
        ...state.items.filters,
        ...payload,
      };
    },
    resetFilters: (state) => {
      state.items.filters = initialState.items.filters;
    },
    setItemDialog: (state, { payload }) => {
      state.item.form = {
        id: payload?.id || "",
        name: payload?.name || "",
        unite: payload?.unite || "",
        price: payload?.price || 0,
        description: payload?.description || "",
        bar_code: payload?.bar_code || "",
        required_serial: payload?.required_serial || false,
      };
      state.item.dialog = !state.item.dialog;
    },
    setPriceDialog: (state, { payload }) => {
      if (!payload) {
        state.priceDialog.open = false;
      } else {
        state.priceDialog.open = true;
        state.priceDialog.data = payload.item_prices || [];
        state.priceDialog.item_id = payload.item_id || payload.id || "";
      }
      state.priceDialog.form = initialState.priceDialog.form;
    },
    setPriceForm: (state, { payload }) => {
      state.priceDialog.form = {
        ...state.priceDialog.form,
        ...payload,
      };
    },
    setPriceLoading: (state, { payload }) => {
      state.priceDialog.loading = payload;
    },
    resetPriceForm: (state) => {
      state.priceDialog.form = initialState.priceDialog.form;
    },
    addPriceData: (state, { payload }) => {
      state.priceDialog.data.push(payload);
    },
    updatePriceData: (state, { payload }) => {
      const index = state.priceDialog.data.findIndex((p) => p.id === payload.id);
      if (index !== -1) {
        state.priceDialog.data[index] = payload;
      }
    },
    removePriceData: (state, { payload }) => {
      state.priceDialog.data = state.priceDialog.data.filter(
        (p) => p.id !== payload,
      );
    },
  },
});

export const {
  setItems,
  setLoading,
  setFilters,
  resetFilters,
  setItemDialog,
  setPriceDialog,
  setPriceForm,
  setPriceLoading,
  resetPriceForm,
  addPriceData,
  updatePriceData,
  removePriceData,
} = itemSlice.actions;

export default itemSlice.reducer;

const index = () => async (dispatch, getState) => {
  try {
    dispatch(setLoading("items"));
    const filters = getState().item.items.filters;
    const res = await itemApi.index(filters);
    dispatch(setItems(res.data));
    dispatch(setLoading("items"));
  } catch (err) {
    dispatch(setLoading("items"));
    throw new Error(err);
  }
};

const getById = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("item"));
    const res = await itemApi.show(id);
    dispatch(setItemDialog(res.data));
    dispatch(setLoading("item"));
  } catch (err) {
    dispatch(setLoading("item"));
    throw new Error(err);
  }
};

const create = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("item"));
    await itemApi.create(data);
    dispatch(setLoading("item"));
    dispatch(
      showNotification({
        message: "تم إضافة المادة بنجاح",
        type: "success",
      }),
    );
    dispatch(setItemDialog());
    dispatch(index());
  } catch (err) {
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    dispatch(setLoading("item"));
    throw new Error(err);
  }
};

const update = (data) => async (dispatch) => {
  try {
    dispatch(setLoading("item"));
    await itemApi.update(data);
    dispatch(setLoading("item"));
    dispatch(
      showNotification({
        message: "تم تعديل المادة بنجاح",
        type: "success",
      }),
    );
    dispatch(setItemDialog());
    dispatch(index());
  } catch (err) {
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    dispatch(setLoading("item"));
    throw new Error(err);
  }
};

const deleteItem = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("items"));
    await itemApi.delete(id);
    dispatch(
      showNotification({
        message: "تم الحذف بنجاح",
        type: "success",
      }),
    );
    dispatch(setLoading("items"));
    return true;
  } catch (err) {
    dispatch(setLoading("items"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

const createPrice = (data) => async (dispatch, getState) => {
  try {
    dispatch(setPriceLoading(true));
    const item_id = getState().item.priceDialog.item_id;
    const res = await itemPriceApi.create({ ...data, item_id });
    dispatch(setPriceLoading(false));
    dispatch(
      showNotification({ message: res.data.message, type: "success" }),
    );
    dispatch(addPriceData(res.data.data[0]));
    dispatch(resetPriceForm());
    return Promise.resolve();  
  } catch (err) {
    dispatch(setPriceLoading(false));
    dispatch(
      showNotification({
        message: "حدث خطأ ما",
        type: "error",
      }),
    );
  }
};

const updatePrice = (data) => async (dispatch, getState) => {
  try {
    dispatch(setPriceLoading(true));
    const item_id = getState().item.priceDialog.item_id;
    const res = await itemPriceApi.update(data);
    dispatch(setPriceLoading(false));
    dispatch(
      showNotification({ message: res.data.message, type: "success" }),
    );
    dispatch(updatePriceData(res.data.data[0]));
    dispatch(resetPriceForm());
     return Promise.resolve();
  } catch (err) {
    dispatch(setPriceLoading(false));
    dispatch(
      showNotification({
        message: "حدث خطأ ما",
        type: "error",
      }),
    );
  }
};

const deletePrice = (id) => async (dispatch, getState) => {
  try {
    const item_id = getState().item.priceDialog.item_id;
    await itemPriceApi.delete(id);
    dispatch(
      showNotification({ message: "تم حذف السعر بنجاح", type: "success" }),
    );
    dispatch(removePriceData(id));
  } catch (err) {
    dispatch(
      showNotification({
        message: "حدث خطأ ما",
        type: "error",
      }),
    );
  }
};

export const Items = {
  index,
  getById,
  create,
  update,
  deleteItem,
  createPrice,
  updatePrice,
  deletePrice,
};
